import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";

export const Dialog        = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogPortal  = RadixDialog.Portal;
export const DialogClose   = RadixDialog.Close;

export const DialogOverlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(({ className, ...p }, ref) => (
  <RadixDialog.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]",
      "data-[state=open]:animate-[fade-in_var(--motion-base)_var(--ease-out-soft)_both]",
      className,
    )}
    {...p}
  />
));
DialogOverlay.displayName = "DialogOverlay";

export const DialogContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(({ className, children, ...p }, ref) => {
  const { t } = useTranslation();
  return (
  <DialogPortal>
    <DialogOverlay />
    <RadixDialog.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2",
        "rounded-[var(--radius-xl)] bg-[var(--bg-elevated)] border border-[var(--border)] shadow-[var(--shadow-pop)]",
        "outline-none",
        "data-[state=open]:animate-[fade-in_var(--motion-base)_var(--ease-out-soft)_both]",
        className,
      )}
      {...p}
    >
      {children}
      <RadixDialog.Close
        aria-label={t("common.close")}
        className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        <X size={15} />
      </RadixDialog.Close>
    </RadixDialog.Content>
  </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

export const DialogHeader = ({
  className, ...p
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-5 pt-5 pb-3", className)} {...p} />
);

export const DialogTitle = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...p }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn("text-[17px] font-semibold tracking-[-0.012em] text-[var(--fg)]", className)}
    {...p}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...p }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn("mt-1 text-[13.5px] text-[var(--fg-subtle)]", className)}
    {...p}
  />
));
DialogDescription.displayName = "DialogDescription";

export const DialogBody = ({
  className, ...p
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-5 py-4", className)} {...p} />
);

export const DialogFooter = ({
  className, ...p
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "px-5 py-3 border-t border-[var(--separator)] flex items-center justify-end gap-2",
      className,
    )}
    {...p}
  />
);
